import { useState, useCallback, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { APIError, useAxios } from "../core/_axios";
import LoadingSpinner from "./LoadingSpinner";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import weekday from "dayjs/plugin/weekday";
import dayOfYear from "dayjs/plugin/dayOfYear";
import { getIPads } from "../core/_requests_v2";
import { IpadFast } from "../core/_models_fast";

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(dayOfYear);

type GroupBy = "" | "week" | "month" | "year";

function groupData(
    data: { date: string; count: number }[],
    groupBy: GroupBy
): { date: string; count: number }[] {
    if (!groupBy) return data;

    const grouped: Record<string, number> = {};

    data.forEach(({ date, count }) => {
        let key = date;
        if (groupBy === "week") {
            key = dayjs(date).startOf("week").format("YYYY-MM-DD");
        } else if (groupBy === "month") {
            key = dayjs(date).format("YYYY-MM");
        } else if (groupBy === "year") {
            key = dayjs(date).format("YYYY");
        }
        grouped[key] = (grouped[key] || 0) + count;
    });

    return Object.entries(grouped)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));
}

export function IPadsGraph() {
    const axios = useAxios();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [rawData, setRawData] = useState<{ date: string, count: number }[]>([]);
    const [groupBy, setGroupBy] = useState<GroupBy>("month");

    const loadiPads = useCallback(async () => {
        setLoadingError(null);
        setLoading(true);
        try {
            const { ipads } = await getIPads(axios);
            
            // Create daily counts of iPads added
            const dailyCounts: Record<string, number> = {};
            ipads.forEach(iPad => {
                const date = dayjs(iPad.inserted_at).format("YYYY-MM-DD");
                dailyCounts[date] = (dailyCounts[date] || 0) + 1;
            });
            
            // Convert to array and sort
            const formattedData = Object.entries(dailyCounts)
                .map(([date, count]) => ({ date, count }))
                .sort((a, b) => a.date.localeCompare(b.date));
            
            // Create cumulative data
            let cumulative = 0;
            const cumulativeData = formattedData.map(item => {
                cumulative += item.count;
                return { date: item.date, count: cumulative };
            });
            
            // Ensure we have data up to today
            const today = dayjs().format("YYYY-MM-DD");
            const lastDate = cumulativeData.length > 0 ? cumulativeData[cumulativeData.length - 1].date : today;
            if (lastDate < today && cumulativeData.length > 0) {
                // Add today's date with the same cumulative count
                cumulativeData.push({ 
                    date: today, 
                    count: cumulativeData[cumulativeData.length - 1].count 
                });
            }
            
            setRawData(cumulativeData);
        } catch (exception) {
            console.error(exception);
            const errorMessage =
                exception instanceof APIError
                    ? exception.message
                    : "An error occurred while loading iPads. Please try again.";
            setLoadingError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [axios]);

    useEffect(() => {
        loadiPads();
    }, [loadiPads]);

    // Fill in missing periods with zeros and add projections
    const fillMissingPeriods = (data: { date: string; count: number }[]): { date: string; count: number; actualCount?: number; isProjected?: boolean }[] => {
        if (data.length === 0) return data;
        
        const result: { date: string; count: number; actualCount?: number; isProjected?: boolean }[] = [];
        
        if (groupBy === "month") {
            const monthMap = new Map(data.map(d => [d.date, d.count]));
            
            // Find the earliest and latest dates
            const dates = data.map(d => d.date).sort();
            const startDate = dayjs(dates[0]);
            const currentMonth = dayjs().format('YYYY-MM');
            const endDate = dayjs(currentMonth);
            
            // Get the last value to carry forward
            let lastValue = 0;
            
            // Generate all months from start to current
            let current = startDate;
            while (current.isBefore(endDate) || current.format('YYYY-MM') === currentMonth) {
                const monthKey = current.format('YYYY-MM');
                const count = monthMap.get(monthKey);
                
                if (count !== undefined) {
                    lastValue = count;
                    
                    if (monthKey === currentMonth) {
                        // For current month, show projected value
                        const dayOfMonth = dayjs().date();
                        const daysInMonth = dayjs().daysInMonth();
                        
                        // Calculate growth rate from previous month if available
                        const prevMonth = current.subtract(1, 'month').format('YYYY-MM');
                        const prevCount = monthMap.get(prevMonth) || 0;
                        const dailyAdditions = prevCount > 0 ? (count - prevCount) / dayOfMonth : count / dayOfMonth;
                        const projectedTotal = count + (dailyAdditions * (daysInMonth - dayOfMonth));
                        
                        if (projectedTotal > count) {
                            result.push({ 
                                date: monthKey, 
                                count: Math.round(projectedTotal), 
                                actualCount: count, 
                                isProjected: true 
                            });
                        } else {
                            result.push({ date: monthKey, count });
                        }
                    } else {
                        result.push({ date: monthKey, count });
                    }
                } else {
                    // Carry forward the last known value for missing months
                    result.push({ date: monthKey, count: lastValue });
                }
                
                current = current.add(1, 'month');
            }
        } else if (groupBy === "year") {
            const yearMap = new Map(data.map(d => [d.date, d.count]));
            
            // Find the earliest and latest years
            const years = data.map(d => d.date).sort();
            const startYear = years[0];
            const currentYear = dayjs().format('YYYY');
            
            let lastValue = 0;
            
            for (let year = parseInt(startYear); year <= parseInt(currentYear); year++) {
                const yearKey = String(year);
                const count = yearMap.get(yearKey);
                
                if (count !== undefined) {
                    lastValue = count;
                    
                    if (yearKey === currentYear) {
                        // For current year, show projected value
                        const dayOfYear = dayjs().dayOfYear();
                        const daysInYear = dayjs().endOf('year').dayOfYear();
                        
                        // Calculate growth rate
                        const prevYear = String(year - 1);
                        const prevCount = yearMap.get(prevYear) || 0;
                        const dailyAdditions = prevCount > 0 ? (count - prevCount) / dayOfYear : count / dayOfYear;
                        const projectedTotal = count + (dailyAdditions * (daysInYear - dayOfYear));
                        
                        if (projectedTotal > count) {
                            result.push({ 
                                date: yearKey, 
                                count: Math.round(projectedTotal), 
                                actualCount: count, 
                                isProjected: true 
                            });
                        } else {
                            result.push({ date: yearKey, count });
                        }
                    } else {
                        result.push({ date: yearKey, count });
                    }
                } else {
                    result.push({ date: yearKey, count: lastValue });
                }
            }
        } else {
            // For week or day view, just return the data as is
            return data.map(d => ({ ...d }));
        }
        
        return result;
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (loadingError) {
        return (
            <div>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">{loadingError}</span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={() => {
                        setLoadingError(null);
                        loadiPads();
                    }}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const groupedData = groupData(rawData, groupBy);
    const processedData = fillMissingPeriods(groupedData);
    
    // Get current period data for display
    const currentPeriodData = processedData[processedData.length - 1];
    
    // Get current period label and count for subtitle
    const getCurrentPeriodLabel = () => {
        const current = currentPeriodData;
        if (!current) return null;
        
        let label = "";
        let value = current.isProjected ? current.actualCount : current.count;
        let projected = current.isProjected ? current.count : null;
        
        if (groupBy === "") {
            label = "Today";
        } else if (groupBy === "week") {
            label = "This week";
        } else if (groupBy === "month") {
            label = "This month";
        } else if (groupBy === "year") {
            label = "This year";
        }
        
        if (projected) {
            return `${value} ${label.toLowerCase()} • Projected: ${projected}`;
        }
        return `${value} ${label.toLowerCase()}`;
    };

    const formatXAxisTick = (value: string) => {
        if (groupBy === "year") {
            return value;
        } else if (groupBy === "month") {
            return dayjs(value).format("MMM 'YY");
        } else if (groupBy === "week") {
            return dayjs(value).format("MMM DD");
        }
        return dayjs(value).format("MMM DD");
    };

    const formatTooltipLabel = (value: string) => {
        if (groupBy === "year") {
            return value;
        } else if (groupBy === "month") {
            return dayjs(value).format("MMMM YYYY");
        }
        return dayjs(value).format("MMM DD, YYYY");
    };

    return (
        <div className="grid gap-5 lg:gap-7.5">
            <div className="card">
                <div className="card-header">
                    <div>
                        <h3 className="card-title">iPads Added Over Time</h3>
                        {currentPeriodData && (
                            <p className="text-sm text-gray-500 mt-1">{getCurrentPeriodLabel()}</p>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setGroupBy("")}
                            className={`btn btn-sm ${groupBy === "" ? "btn-primary" : "btn-light"}`}
                        >
                            Day
                        </button>
                        <button
                            onClick={() => setGroupBy("week")}
                            className={`btn btn-sm ${groupBy === "week" ? "btn-primary" : "btn-light"}`}
                        >
                            Week
                        </button>
                        <button
                            onClick={() => setGroupBy("month")}
                            className={`btn btn-sm ${groupBy === "month" ? "btn-primary" : "btn-light"}`}
                        >
                            Month
                        </button>
                        <button
                            onClick={() => setGroupBy("year")}
                            className={`btn btn-sm ${groupBy === "year" ? "btn-primary" : "btn-light"}`}
                        >
                            Year
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    <ResponsiveContainer width="100%" height={500}>
                        <LineChart data={processedData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="date" 
                                tick={{ fontSize: 12 }}
                                tickFormatter={formatXAxisTick}
                            />
                            <YAxis />
                            <Tooltip
                                labelFormatter={formatTooltipLabel}
                                formatter={(value: any, name: string, props: any) => {
                                    if (props.payload.isProjected) {
                                        return [
                                            `${value} iPads (projected)`,
                                            `Current: ${props.payload.actualCount}`
                                        ];
                                    }
                                    return [`${value} iPads`, "Total"];
                                }}
                            />
                            <Line 
                                type="monotone" 
                                dataKey="count" 
                                stroke="#8884d8"
                                strokeWidth={2}
                                dot={(props: any) => {
                                    if (props.payload.isProjected) {
                                        return (
                                            <circle
                                                cx={props.cx}
                                                cy={props.cy}
                                                r={4}
                                                fill="#8884d8"
                                                strokeDasharray="3 3"
                                            />
                                        );
                                    }
                                    return <circle cx={props.cx} cy={props.cy} r={4} fill="#8884d8" />;
                                }}
                                activeDot={{ r: 6 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}